import { memo, useMemo } from 'react';

import { WorkspaceAgentChangePreviewPillTableHost } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentChangePreviewPillTableHost';
import { WorkspaceAgentBeforeAfterTable } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/tables/WorkspaceAgentBeforeAfterTable';
import { type ChangePreviewPill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import {
  formatWorkspaceAgentChangePreviewPillCaption,
  mapWorkspaceAgentChangePreviewPillRowsToBeforeAfterTable,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentChangePreviewPillUtils';

export type WorkspaceAgentChangePreviewPillProps = {
  'data-testid'?: string;
  pill: ChangePreviewPill;
};

export const WorkspaceAgentChangePreviewPill = memo(
  ({ 'data-testid': testId = 'WORKSPACE_AGENT_CHANGE_PREVIEW_PILL', pill }: WorkspaceAgentChangePreviewPillProps) => {
    const tableRows = useMemo(() => mapWorkspaceAgentChangePreviewPillRowsToBeforeAfterTable(pill.rows), [pill.rows]);
    const caption = useMemo(() => formatWorkspaceAgentChangePreviewPillCaption(pill), [pill]);

    return (
      <div
        className="w-full min-w-0 max-w-full rounded-6 ads:bg-raised bg-gray-2 px-2.5 py-2 ads:ring-subtle ring-1 ring-gray-3 ring-inset"
        data-testid={testId}
      >
        <p
          className="mb-2 line-clamp-2 break-words font-medium ads:text-secondary text-12 text-gray-11 leading-4"
          title={pill.title}
        >
          {pill.title}
        </p>
        <WorkspaceAgentChangePreviewPillTableHost>
          <WorkspaceAgentBeforeAfterTable
            caption={caption}
            data-testid="WORKSPACE_AGENT_CHANGE_PREVIEW_PILL_TABLE"
            regionLabel={pill.title}
            rows={tableRows}
          />
        </WorkspaceAgentChangePreviewPillTableHost>
        {pill.warnings && pill.warnings.length > 0 ? (
          <ul className="mt-2 list-disc space-y-1 pl-4 ads:text-metadata text-12 text-gray-10 leading-4">
            {pill.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentChangePreviewPill.displayName = 'WorkspaceAgentChangePreviewPill';
